export {SongIdea as Page};

import {Helmet} from "react-helmet-async";
import {useEffect, useState, useContext} from "preact/hooks";
import {AccountSidebar} from "../../../components/AccountSidebar.jsx";
import {ContentLocked} from "../../../components/ContentLocked.jsx";
import messages from "../../../messages/messages.js";
import API_URL from "../../../includes/api.js";
import {NotificationContext} from '../../../components/NotificationContext.jsx';
import {useAuth} from '../../../components/AuthProvider.jsx';
import {CircleSpinner, RingSpinner} from '../../../components/Spinner.jsx';

function SongIdea() {

    const {addNotification} = useContext(NotificationContext);
    const {getToken, user, authLoading} = useAuth();

    const [allowed, setAllowed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [genres, setGenres] = useState(null);
    const [genre, setGenre] = useState('');
    const [keywords, setKeywords] = useState('');
    const [idea, setIdea] = useState([]);

    useEffect(() => {
        if (authLoading) return;

        if (!user) return;

        getToken().then(token => {
            fetch(API_URL + "/idea/genres?firebase_auth_token=" + token)
                .then(response => response.json()).then(data => {
                if (data.code === 200) {
                    setAllowed(true);
                    setGenres(data.data);
                } else {
                    setAllowed(false);
                }
            }).finally(() => {
                setPageLoading(false);
            });
        });
    }, [authLoading, user])

    async function handleSubmit(e) {
        e.preventDefault();
        setLoading(true);
        const token = await getToken();
        fetch(API_URL + "/idea/get?firebase_auth_token=" + token + "&genre=" + genre + "&keywords=" + keywords)
            .then(response => response.json()).then(data => {
                if (data.code === 200) {
                    setIdea(data.data);
                } else {
                    addNotification(data.message, "error");
                }
            }
        ).catch(() => {
            addNotification("An error occurred while generating the song idea.", "error");
        }).finally(() => {
            setLoading(false);
        });
    }

    return (
        <section className="text-white">

            <Helmet>
                <title>{messages["SITE.NAME"]} | Song Idea Generator</title>
                <meta name="description" content="Song Idea Generator"/>
            </Helmet>

            <div>

                <div className="flex flex-wrap">

                    <AccountSidebar/>

                    {pageLoading ? (
                        <div className="w-full md:w-3/4 px-0 md:px-4">
                            <div className="p-6 text-white rounded-lg bg-zinc-800">
                                <div className="flex items-center">
                                    <CircleSpinner/>
                                    <h3 className="text-lg m-0">Loading...</h3>
                                </div>
                            </div>
                        </div>
                    ) : (
                        !allowed ? (
                            <div className="w-full md:w-3/4 px-0 md:px-4">
                                <ContentLocked/>
                            </div>
                        ) : (
                            <div className="w-full md:w-3/4 px-0 md:px-4">
                                <div className="border border-border/60 bg-zinc-800 rounded-lg p-6 mb-4">
                                    <h3 className="text-xl mt-0 mb-2">Song Idea Generator</h3>
                                    <p className="mb-4 text-sm text-gray-400">Get song ideas based on your mood, genre,
                                        or any other input. Perfect for overcoming writer's block and sparking
                                        creativity.</p>

                                    <form onSubmit={handleSubmit}>
                                        <div className="flex flex-wrap -mx-3 mb-4">
                                            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                                <label htmlFor="genre"
                                                       className="block mb-2 text-sm font-medium text-white">Genre</label>
                                                <select
                                                    id="genre"
                                                    name="genre"
                                                    value={genre}
                                                    onChange={e => setGenre(e.target.value)}
                                                    className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                                >
                                                    <option value="">Select a genre</option>
                                                    {genres && genres.map(g => (
                                                        <option value={g} key={g}>{g}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            <div className="w-full md:w-1/2 px-3 mb-6 md:mb-0">
                                                <label htmlFor="keywords"
                                                       className="block mb-2 text-sm font-medium text-white">Keywords
                                                    (comma separated)</label>
                                                <input
                                                    type="text"
                                                    name="keywords"
                                                    id="keywords"
                                                    value={keywords}
                                                    onChange={e => setKeywords(e.target.value)}
                                                    className="bg-gray-50 border border-gray-300 text-gray-900 sm:text-sm rounded-lg block w-full p-2.5"
                                                    placeholder="e.g. happy, love, summer"
                                                />
                                            </div>
                                        </div>
                                        <button type="submit"
                                                className="text-white cursor-pointer border border-white hover:bg-white hover:text-black font-medium rounded-lg text-sm px-10 py-2.5 text-center">Discover {loading && (
                                            <RingSpinner/>)}
                                        </button>
                                    </form>

                                    {idea && idea.length > 0 && (
                                        <div className="relative">
                                            {loading && (
                                                <div className="absolute inset-0 bg-[#000000ad] z-10 flex items-center justify-center">
                                                    <CircleSpinner className="animate-spin h-8 w-8 text-white"/>
                                                </div>
                                            )}
                                            <h4 className="text-lg font-semibold mt-6 mb-2">Generated Song Ideas:</h4>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
                                                {idea.map((item, index) => (
                                                    <div key={index} className="w-full">
                                                        <span>{item.title}</span>
                                                        <iframe data-testid="embed-iframe" style="border-radius:12px"
                                                                title={`Spotify preview for ${item.title}`}
                                                                src={"https://open.spotify.com/embed/track/" + item.id + "?utm_source=generator"}
                                                                width="100%" height="80" frameBorder="0"
                                                                allowFullScreen=""
                                                                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                                                                loading="lazy"></iframe>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}

                </div>

            </div>
        </section>
    );
}
